import { ApolloError } from '@apollo/client';
import { Request } from '@99designs/graph-utils/types';
import { CollaborationListItemFragment } from '../queries/ListingCollaborations.generated';
import { isPastCollaboration } from '../utils/isPastCollaboration';
import { Project, workItemToProject } from '../utils/project.transformer';
import { useProjectData } from './useProjectData';

export type CombinedProject =
    | { type: 'Project'; value: Project }
    | { type: 'Collaboration'; value: CollaborationListItemFragment }
    | { type: 'Request'; value: Request }
    | { type: 'Legacy'; value: LegacyRequest }
    | { type: 'Processing'; value: Request };

type CombinedProjects = {
    current: CombinedProject[];
    past: CombinedProject[];
    error?: ApolloError;
    loading: boolean;
};

export type LegacyRequest = {
    publicId: string;
    type: string;
    createdAt?: string | null;
    title: string;
    previewRef: string;
    downloads: string[];
};

function transformProject(project: Project): CombinedProject {
    return { type: 'Project', value: project };
}

function transformCollaboration(collaboration: CollaborationListItemFragment): CombinedProject {
    return { type: 'Collaboration', value: collaboration };
}

function transformRequest(request: Request): CombinedProject {
    return { type: 'Request', value: request };
}

function transformLegacyRequest(request: LegacyRequest): CombinedProject {
    return { type: 'Legacy', value: request };
}

function transformProcessing(request: Request): CombinedProject {
    return { type: 'Processing', value: request };
}

function getProjectDate(project: CombinedProject): Date | undefined {
    if (project.type === 'Collaboration') {
        return new Date(project.value.createdAt);
    }
    if (project.type === 'Request') {
        return project.value.job ? new Date(project.value.job.createdAt) : undefined;
    }
    if (project.type === 'Legacy') {
        return project.value.createdAt ? new Date(project.value.createdAt) : undefined;
    }
    if (project.type === 'Processing') {
        return project.value.createdAt ? new Date(project.value.createdAt) : undefined;
    }

    return new Date(project.value.createdDateTimestamp);
}

function sortProjects(a: CombinedProject, b: CombinedProject): number {
    return (
        (getProjectDate(b) || new Date()).getTime() - (getProjectDate(a) || new Date()).getTime()
    );
}

export function useCombinedProjects(): CombinedProjects {
    const { workItems, collaborations, legacy, jobs, processing, error, loading } =
        useProjectData();

    const projects = workItems.flatMap(workItemToProject).reduce(
        (acc, project) => {
            if (project.section === 'current') {
                acc.current.push(project);
            } else {
                acc.past.push(project);
            }
            return acc;
        },
        {
            current: [],
            past: [],
        } as {
            current: Project[];
            past: Project[];
        }
    );

    const currentCollaborations = collaborations.filter((c) => !isPastCollaboration(c));
    const pastCollaborations = collaborations.filter((c) => isPastCollaboration(c));

    return {
        current: [
            ...processing.map(transformProcessing),
            ...jobs.map(transformRequest), // if a job is in the matching phase, it should be shown as a current project
            ...currentCollaborations.map(transformCollaboration),
            ...projects.current.map(transformProject),
        ].sort(sortProjects),
        past: [
            ...pastCollaborations.map(transformCollaboration),
            ...legacy.map(transformLegacyRequest),
            ...projects.past.map(transformProject),
        ].sort(sortProjects),
        error,
        loading,
    };
}
